import type { Product, Script } from "@easyshot/shared";
// Keep this helper usable in the Worker without importing React/TSX modules.
interface ProductMediaItem { id: string; kind: "photo" | "video"; url: string; filename: string }

const MIN_DURATION_SECONDS = 3;
const MAX_DURATION_SECONDS = 300;

/**
 * EasyShot allows up to five minutes on its active RunPod route. English is
 * estimated by words, while CJK speech is estimated by visible characters.
 */
export function estimateInfiniteTalkDuration(text: string, maxSeconds = MAX_DURATION_SECONDS): number {
  const normalized = text.trim();
  if (!normalized) return MIN_DURATION_SECONDS;
  const cjkCharacters = normalized.match(/[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/gu)?.length ?? 0;
  const latinWords = normalized
    .replace(/[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/gu, " ")
    .split(/\s+/)
    .filter(Boolean).length;
  const seconds = Math.ceil(cjkCharacters / 3.5 + latinWords / 2.4);
  return Math.min(maxSeconds, Math.max(MIN_DURATION_SECONDS, seconds));
}

export function buildInfiniteTalkScript(text: string): Script {
  const narration = text.trim();
  const duration = estimateInfiniteTalkDuration(narration);
  return {
    id: `sc_${Date.now().toString(36)}`,
    hook: "",
    scenes: [
      {
        id: `sn_${Date.now().toString(36)}`,
        narration,
        visual: "Natural direct-to-camera talking avatar with stable framing.",
        durationSec: duration,
      },
    ],
    cta: "",
    totalDurationSec: duration,
    language: /[\p{Script=Han}]/u.test(narration) ? "zh-CN" : "en-US",
  };
}

export function buildInfiniteTalkProduct(
  current: Product | null,
  background?: ProductMediaItem,
  removeAvatarBackground = false,
): Product {
  return {
    id: current?.id ?? `pd_${Date.now().toString(36)}`,
    title: current?.title || "Talking Video",
    description: current?.description || "Talking avatar presentation",
    sellingPoints: current?.sellingPoints ?? [],
    images: background?.kind === "photo" ? [background.url] : [],
    backgroundMedia:
      background?.kind === "photo"
        ? {
            id: background.id,
            kind: "photo",
            url: background.url,
            filename: background.filename,
          }
        : undefined,
    removeAvatarBackground,
  };
}
